import React, { useState, useRef, useCallback, useEffect } from "react";
import api from "../api";

/**
 * VideoTrimmer — Basic video trim (start/end), crop, and transition effects.
 * Works with uploaded MP4 files. Exports trimmed video via backend ffmpeg.
 * 
 * Props:
 *   videoUrl: string — source video URL
 *   onSave: (newUrl: string) => void
 *   onClose: () => void
 */
export default function VideoTrimmer({ videoUrl, onSave, onClose }) {
  const videoRef = useRef(null);
  const timelineRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("trim"); // trim | transition
  const [transition, setTransition] = useState("none"); // none | fade | dissolve
  const [fadeIn, setFadeIn] = useState(0);
  const [fadeOut, setFadeOut] = useState(0);

  const onLoaded = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    setDuration(v.duration);
    setTrimEnd(v.duration);
  }, []);

  const onTimeUpdate = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    setCurrentTime(v.currentTime);
    if (v.currentTime >= trimEnd) { v.pause(); setPlaying(false); }
  }, [trimEnd]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) {
      if (v.currentTime < trimStart || v.currentTime >= trimEnd) v.currentTime = trimStart;
      v.play(); setPlaying(true);
    } else {
      v.pause(); setPlaying(false);
    }
  }, [trimStart, trimEnd]);

  const seekTo = (t) => {
    const v = videoRef.current; if (!v) return;
    v.currentTime = Math.max(0, Math.min(duration, t));
  };

  const handleTimelineClick = (e) => {
    const rect = timelineRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seekTo(pct * duration);
  };

  const fmt = (t) => {
    const m = Math.floor(t / 60); const s = Math.floor(t % 60);
    const ms = Math.floor((t % 1) * 10);
    return `${m}:${String(s).padStart(2, "0")}.${ms}`;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/api/admin/trim-video", {
        video_url: videoUrl,
        trim_start: trimStart,
        trim_end: trimEnd,
        fade_in: transition !== "none" ? fadeIn : 0,
        fade_out: transition !== "none" ? fadeOut : 0,
        transition,
      }, { timeout: 120000 });
      onSave(data.url);
      onClose();
    } catch (err) {
      alert("영상 편집 실패: " + (err.response?.data?.error || err.message));
    }
    setSaving(false);
  };

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); if (e.code === "Space") { e.preventDefault(); togglePlay(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, togglePlay]);

  const trimPct = (t) => duration > 0 ? (t / duration) * 100 : 0;
  const hasChanges = trimStart > 0.1 || (duration > 0 && trimEnd < duration - 0.1) || transition !== "none";

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-[#1d1d1f] rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 shrink-0">
          <button onClick={onClose} className="text-sm text-white/60 hover:text-white transition">취소</button>
          <h3 className="text-sm font-bold text-white">영상 편집</h3>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving || !hasChanges}
              className="px-4 py-1.5 bg-[#0071e3] text-white text-sm font-semibold rounded-lg hover:bg-[#0077ED] disabled:opacity-40 transition">
              {saving ? "처리 중..." : "내보내기"}
            </button>
          </div>
        </div>

        {/* Preview */}
        <div className="flex-1 min-h-0 flex items-center justify-center p-4 bg-black relative">
          <video ref={videoRef} src={videoUrl} className="max-h-[50vh] max-w-full rounded-lg"
            muted playsInline onLoadedMetadata={onLoaded} onTimeUpdate={onTimeUpdate}
            style={{ opacity: (transition !== "none" && fadeIn > 0 && currentTime < trimStart + fadeIn)
              ? Math.min(1, (currentTime - trimStart) / fadeIn) : 1 }} />
          <button onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition">
            <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <span className="text-white text-2xl">{playing ? "⏸" : "▶"}</span>
            </div>
          </button>
        </div>

        {/* Timeline */}
        <div className="px-5 py-3 border-t border-white/10">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] text-white/50 tabular-nums w-14">{fmt(currentTime)}</span>
            <div ref={timelineRef} className="flex-1 h-10 bg-white/5 rounded-lg relative cursor-pointer" onClick={handleTimelineClick}>
              {/* Trim region */}
              <div className="absolute top-0 bottom-0 bg-[#0071e3]/30 rounded" style={{ left: `${trimPct(trimStart)}%`, width: `${trimPct(trimEnd) - trimPct(trimStart)}%` }} />
              {/* Trim handles */}
              <div className="absolute top-0 bottom-0 w-1.5 bg-[#0071e3] rounded cursor-col-resize"
                style={{ left: `${trimPct(trimStart)}%` }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const rect = timelineRef.current.getBoundingClientRect();
                  const onMove = (ev) => { const p = Math.max(0, Math.min((ev.clientX - rect.left) / rect.width, trimPct(trimEnd) / 100 - 0.01)); setTrimStart(p * duration); };
                  const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
                  window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
                }} />
              <div className="absolute top-0 bottom-0 w-1.5 bg-[#0071e3] rounded cursor-col-resize"
                style={{ left: `${trimPct(trimEnd)}%` }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  const rect = timelineRef.current.getBoundingClientRect();
                  const onMove = (ev) => { const p = Math.max(trimPct(trimStart) / 100 + 0.01, Math.min(1, (ev.clientX - rect.left) / rect.width)); setTrimEnd(p * duration); };
                  const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
                  window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
                }} />
              {/* Playhead */}
              <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg" style={{ left: `${trimPct(currentTime)}%` }}>
                <div className="w-2.5 h-2.5 bg-white rounded-full -translate-x-1 -translate-y-0.5" />
              </div>
            </div>
            <span className="text-[10px] text-white/50 tabular-nums w-14 text-right">{fmt(duration)}</span>
          </div>

          {/* Trim info */}
          <div className="flex items-center gap-4 text-[10px] text-white/40">
            <span>시작: <b className="text-white/70">{fmt(trimStart)}</b></span>
            <span>끝: <b className="text-white/70">{fmt(trimEnd)}</b></span>
            <span>길이: <b className="text-white/70">{fmt(trimEnd - trimStart)}</b></span>
          </div>
        </div>

        {/* Tools */}
        <div className="border-t border-white/10 shrink-0">
          <div className="flex border-b border-white/5">
            {[{ key: "trim", icon: "✂️", label: "컷 편집" }, { key: "transition", icon: "🔀", label: "전환 효과" }].map(({ key, icon, label }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`flex-1 py-2.5 text-xs font-semibold transition ${tab === key ? "text-[#0071e3] border-b-2 border-[#0071e3]" : "text-white/40 hover:text-white/60"}`}>
                {icon} {label}
              </button>
            ))}
          </div>
          <div className="px-5 py-3 min-h-[60px]">
            {tab === "trim" && (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <label className="text-[10px] text-white/50">시작</label>
                  <input type="number" step="0.1" min={0} max={trimEnd - 0.1} value={+trimStart.toFixed(1)}
                    onChange={e => { setTrimStart(+e.target.value); seekTo(+e.target.value); }}
                    className="w-16 px-2 py-1 bg-white/10 text-white text-xs rounded-lg text-center tabular-nums border border-white/10" />
                </div>
                <div className="flex items-center gap-1.5">
                  <label className="text-[10px] text-white/50">끝</label>
                  <input type="number" step="0.1" min={trimStart + 0.1} max={duration} value={+trimEnd.toFixed(1)}
                    onChange={e => { setTrimEnd(+e.target.value); seekTo(+e.target.value); }}
                    className="w-16 px-2 py-1 bg-white/10 text-white text-xs rounded-lg text-center tabular-nums border border-white/10" />
                </div>
                <button onClick={() => setTrimStart(currentTime)} className="px-2 py-1 bg-white/10 rounded-lg text-[10px] text-white/60 hover:text-white hover:bg-white/20 transition">현재 위치를 시작점으로</button>
                <button onClick={() => setTrimEnd(currentTime)} className="px-2 py-1 bg-white/10 rounded-lg text-[10px] text-white/60 hover:text-white hover:bg-white/20 transition">현재 위치를 끝점으로</button>
              </div>
            )}
            {tab === "transition" && (
              <div className="space-y-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-white/50 w-12">효과:</span>
                  {[["none", "없음"], ["fade", "페이드"], ["dissolve", "디졸브"]].map(([v, l]) => (
                    <button key={v} onClick={() => setTransition(v)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition ${transition === v ? "bg-[#0071e3] text-white" : "bg-white/10 text-white/50 hover:bg-white/20"}`}>{l}</button>
                  ))}
                </div>
                {transition !== "none" && (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <label className="text-[10px] text-white/50">페이드 인</label>
                      <input type="range" min={0} max={3} step={0.1} value={fadeIn} onChange={e => setFadeIn(+e.target.value)}
                        className="w-24 accent-[#0071e3]" />
                      <span className="text-[10px] text-white/50 w-8 tabular-nums">{fadeIn}s</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <label className="text-[10px] text-white/50">페이드 아웃</label>
                      <input type="range" min={0} max={3} step={0.1} value={fadeOut} onChange={e => setFadeOut(+e.target.value)}
                        className="w-24 accent-[#0071e3]" />
                      <span className="text-[10px] text-white/50 w-8 tabular-nums">{fadeOut}s</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
